import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Moment from 'moment-timezone'
import { parse as qsParse } from 'qs'

import { AutocompleteInput } from '@containers/common/form'
import { InvoicesModal } from '@containers/invoice'
import { DateInput, Input } from '@components/common/form'
import Button from '@components/common/form/Button'
import LinkText from '@components/common/form/LinkText'
import YSpacing from '@components/common/YSpacing'
import XSpacing from '@components/common/XSpacing'
import FlexContainer from '@components/common/FlexContainer'
import Panel from '@components/common/Panel'
import Table from '@components/common/Table'
import { TooltipModal } from '@components/common/modal'
const AllStatuses = ['Proposed', 'Confirmed']
const uuidRegEx =
  /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/

const limit = 25

export const parseUrlForKey = (queryString, key) => {
  const parsed = qsParse(queryString, { ignoreQueryPrefix: true })

  return parsed[key] || ''
}

export const parseQueryStringForOrderIds = (queryString) => {
  const orderIdsString = parseUrlForKey(queryString, 'order_ids')
  let orderIds = null
  if (orderIdsString) {
    orderIds = orderIdsString.split(',')
  }

  return orderIds
}

class InvoicesPage extends Component {
  state = {
    arClientId: undefined,
    preloadOrders: null,
    search: '',
    searchFrom: Moment().startOf('day'),
    searchTo: undefined,
    showEditModal: false,
    invoices: [],
    client: undefined,
    enabled: undefined,
    selectedInvoice: undefined,
    sortBy: [undefined, true],
    statuses: [...AllStatuses],
    offset: 0,
  }
  searchTimer = undefined

  componentDidMount() {
    const { location } = this.props
    if (
      location.search.match(uuidRegEx) &&
      !location.pathname.includes('client')
    ) {
      const [invoiceId] = location.search.match(uuidRegEx)
      this.editInvoice(invoiceId)()
    } else {
      this.searchInvoices() // TODO: may need to disable this if preloading orders
      const preloadOrders = parseQueryStringForOrderIds(location.search)
      const invoicePathBreakdown =
        location.pathname.match(/\/invoices\/([^/]*)/)
      const clientPathBreakdown = location.pathname.match(
        /\/invoices\/clients\/([^/]*)/,
      )
      if (clientPathBreakdown && clientPathBreakdown.length > 1) {
        this.setState(
          { arClientId: clientPathBreakdown[1], preloadOrders },
          () => {
            this.setState({ showEditModal: true, selectedInvoice: undefined })
          },
        )
      } else if (invoicePathBreakdown && invoicePathBreakdown.length > 1) {
        this.editInvoice(invoicePathBreakdown[1])()
      }
    }
  }

  componentDidUpdate = (prevProps, prevState) => {
    const { searchFrom, searchTo } = this.state
    if (
      prevState.searchFrom !== searchFrom ||
      prevState.searchTo !== searchTo
    ) {
      this.searchInvoices()
    }
  }

  searchInvoices = async () => {
    const { searchInvoices } = this.props
    const { searchTo, searchFrom, client, search, offset, orderSearch } =
      this.state

    const invResp = await searchInvoices({
      to: searchTo,
      from: searchFrom,
      clientId: client && client.id,
      search,
      offset,
      limit,
      orderSearch,
    })
    if (invResp) {
      this.setState({ invoices: invResp })
    }
  }

  searchAfterTimeout = () => {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer)
    }
    this.searchTimer = undefined
    this.searchTimer = setTimeout(this.searchInvoices, 650)
  }

  editInvoice = (id) => async () => {
    const invoice = await this.props.getInvoice(id)
    if (invoice) {
      this.setState({
        selectedInvoice: invoice,
        showEditModal: true,
      })
    }
  }

  hideEditModal = () =>
    this.setState({
      showEditModal: false,
      selectedInvoice: undefined,
      preloadOrders: null,
    })

  sortInvoices = (attribute, transform = (a) => a) => {
    const { invoices, sortBy } = this.state
    const asc = attribute !== sortBy[0] || !sortBy[1]
    this.setState({
      sortBy: [attribute, asc],
      invoices: invoices.sort((a, b) => {
        if (transform(a[attribute]) > transform(b[attribute])) {
          return asc ? 1 : -1
        }
        if (transform(a[attribute]) < transform(b[attribute])) {
          return asc ? -1 : 1
        }

        return 0
      }),
    })
  }

  updateInvoices = (inv) => {
    const { invoices } = this.state
    const idx = invoices.findIndex((invoice) => invoice.id === inv.id)
    if (idx === -1) {
      invoices.unshift(inv)
    } else {
      invoices.splice(idx, 1, inv)
    }
    this.setState({ selectedInvoice: inv, invoices, showEditModal: false })
  }

  render() {
    const { theme, invoiceUrl, invoicePDFUrl, searchClients } = this.props
    const {
      arClientId,
      preloadOrders,
      client,
      invoices,
      orderSearch,
      search,
      searchFrom,
      searchTo,
      selectedInvoice,
      showEditModal,
      offset,
    } = this.state

    const page = Math.ceil(offset / limit) + 1

    return (
      <Panel width="100%" maxWidth="900px" heading="Invoices">
        {showEditModal && (
          <InvoicesModal
            arClientId={arClientId}
            invoice={selectedInvoice}
            preloadOrders={preloadOrders}
            theme={theme}
            hideModal={this.hideEditModal}
            searchInvoices={this.searchInvoices}
            updateInvoices={this.updateInvoices}
          />
        )}
        <FlexContainer alignItems="flex-end">
          <AutocompleteInput
            label="Client"
            width="25%"
            displayAttribute="name"
            loaderFunction={(search) => searchClients({ ...search })}
            placeholder={'Search Clients'}
            value={(client && client.name) || ''}
            onSelect={(client) =>
              this.setState({ client, offset: 0 }, this.searchInvoices)
            }
            onClear={() =>
              this.setState(
                { client: undefined, offset: 0 },
                this.searchInvoices,
              )
            }
          />
          <XSpacing width="20px" />
          <DateInput
            width="200px"
            label="Search From"
            date={searchFrom}
            dateFormat="default"
            isStatic={false}
            onChange={(searchFrom) =>
              this.setState({ searchFrom, offset: 0 }, this.searchInvoices)
            }
            clearDate={() =>
              this.setState(
                { searchFrom: undefined, offset: 0 },
                this.searchInvoices,
              )
            }
          />
          <XSpacing width="20px" />
          <DateInput
            width="200px"
            label="Search To"
            date={searchTo}
            dateFormat="default"
            isStatic={false}
            onChange={(searchTo) =>
              this.setState({ searchTo, offset: 0 }, this.searchInvoices)
            }
            clearDate={() =>
              this.setState(
                { searchTo: undefined, offset: 0 },
                this.searchInvoices,
              )
            }
          />
          <XSpacing width="20px" />

          <Button
            label="New Invoice"
            onClick={() =>
              this.setState({ showEditModal: true, selectedInvoice: undefined })
            }
          />
        </FlexContainer>
        <YSpacing height="20px" />

        <FlexContainer alignItems="center" justifyConten="flex-start">
          <Input
            label="Search Batch Invoice Number"
            width="200px"
            type="text"
            value={search}
            onChange={(e) =>
              this.setState(
                { search: e.target.value, offset: 0 },
                this.searchAfterTimeout,
              )
            }
          />
          <XSpacing width="25px" />
          <Input
            label="Search Individual Order Number"
            width="200px"
            type="text"
            value={orderSearch}
            onChange={(e) =>
              this.setState(
                { orderSearch: e.target.value, offset: 0 },
                this.searchAfterTimeout,
              )
            }
          />
          <TooltipModal
            unicode="&#9432;"
            width="200px"
            information={
              'To confirm if an individual invoice has already been added to a batch invoice, enter your order number here. Please note: An invoice can only be added to one batch invoice at a time.'
            }
          />
        </FlexContainer>
        <YSpacing height="20px" />

        <FlexContainer alignItems="flex-end">
          <FlexContainer>Page {page}</FlexContainer>

          <XSpacing width="20px" />
          {offset > 0 && (
            <Button
              label="<"
              onClick={() =>
                this.setState(
                  { offset: this.state.offset - limit },
                  this.searchInvoices,
                )
              }
            />
          )}
          <XSpacing width="20px" />
          {invoices.length === limit && (
            <Button
              label=">"
              onClick={() =>
                this.setState(
                  { offset: this.state.offset + limit },
                  this.searchInvoices,
                )
              }
            />
          )}
        </FlexContainer>
        <YSpacing height="20px" />

        <Table>
          <tr>
            <th onClick={() => this.sortInvoices('invoiceNumber')}>
              Invoice Num
            </th>
            <th
              onClick={() =>
                this.sortInvoices('createdAt', (crAt) => Moment(crAt).unix())
              }
            >
              Created At
            </th>
            <th
              onClick={() => this.sortInvoices('buyer', (buyer) => buyer.name)}
            >
              Client
            </th>
            <th
              onClick={() =>
                this.sortInvoices('totalDue', (x) => parseFloat(x))
              }
            >
              Total Due
            </th>
            <th
              onClick={() =>
                this.sortInvoices('totalPaid', (x) => parseFloat(x))
              }
            >
              Total Paid
            </th>
            <th />
            <th />
            <th />
          </tr>
          {invoices.map((invoice) => {
            const isOverPay =
              Array.isArray(invoice.overPayOrders) &&
              invoice.overPayOrders.length > 0

            return (
              <tr
                key={invoice.id}
                style={{ background: `${isOverPay ? '#FF0000' : '#FFFFFF'}` }}
              >
                <td>
                  <p>{invoice.invoiceNumber}</p>
                </td>
                <td>
                  <p>{Moment(invoice.createdAt).format('lll')}</p>
                </td>
                <td>
                  <p>{invoice.buyer && invoice.buyer.name}</p>
                </td>
                <td>
                  <p>{`$${parseFloat(invoice.totalDue).toFixed(2)}`}</p>
                </td>
                <td>
                  <p>{`$${parseFloat(invoice.totalPaid).toFixed(2)}`}</p>
                </td>
                <td>
                  <LinkText
                    isLink={true}
                    link={`${invoiceUrl}/${invoice.id}`}
                    label="Pay Now"
                  />
                </td>
                <td>
                  <LinkText
                    label="Edit"
                    onClick={this.editInvoice(invoice.id)}
                  />
                </td>
                <td>
                  <LinkText
                    isLink={true}
                    link={`${invoicePDFUrl}/${invoice.id}/batch_invoice_pdf`}
                    label="PDF"
                  />
                </td>
              </tr>
            )
          })}
        </Table>
      </Panel>
    )
  }
}

InvoicesPage.propTypes = {
  headquarter: PropTypes.number,
  theme: PropTypes.object,
  invoiceUrl: PropTypes.string,
  invoicePDFUrl: PropTypes.string,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.object,
  }),

  getInvoice: PropTypes.func,
  searchClients: PropTypes.func,
  searchInvoices: PropTypes.func,
}

export default InvoicesPage
